import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/env';

const BASE_URL = API_BASE_URL;
const api = axios.create({
    baseURL: BASE_URL
});

export const axiosPrivate = axios.create({
    baseURL: BASE_URL,
});

const AUTH_REFRESH_ENDPOINT = '/auth/refresh';
let refreshPromise: Promise<string | null> | null = null;

const clearAuthStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('cartCount');
};

const handleUnauthorized = () => {
    clearAuthStorage();

    const currentPath = window.location.pathname;
    const loginPath = currentPath.startsWith('/admin')
        ? '/admin/login'
        : '/login';
    if (currentPath !== loginPath) {
        window.location.href = loginPath;
    }
};

const isExcludedFromRefresh = (url?: string) => {
    if (!url) return false;
    return (
        url.includes('/auth/customer/login')
        || url.includes('/auth/admin/login')
        || url.includes('/auth/delivery-boy/login')
        || url.includes('/auth/login')
        || url.includes('/auth/register')
        || url.includes(AUTH_REFRESH_ENDPOINT)
    );
};

const requestTokenRefresh = async () => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
        return null;
    }

    try {
        const response = await axios.post(`${BASE_URL}${AUTH_REFRESH_ENDPOINT}`, {
            refreshToken: storedRefreshToken,
        });

        const nextAccessToken = response.data?.accessToken ? String(response.data.accessToken) : '';
        const nextRefreshToken = response.data?.refreshToken ? String(response.data.refreshToken) : '';

        if (!nextAccessToken || !nextRefreshToken) {
            return null;
        }

        localStorage.setItem('token', nextAccessToken);
        localStorage.setItem('refreshToken', nextRefreshToken);
        return nextAccessToken;
    } catch {
        return null;
    }
};

const attachRequestInterceptor = (client: typeof api) => {
    client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        if (!isExcludedFromRefresh(config.url)) {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    });
};

const attachAuthExpiryInterceptor = (client: typeof api) => {
    client.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const status = error?.response?.status;
            const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

            if (status !== 401 || !originalRequest) {
                return Promise.reject(error);
            }

            if (originalRequest._retry || isExcludedFromRefresh(originalRequest.url)) {
                handleUnauthorized();
                return Promise.reject(error);
            }

            if (!refreshPromise) {
                refreshPromise = requestTokenRefresh().finally(() => {
                    refreshPromise = null;
                });
            }

            const nextAccessToken = await refreshPromise;
            if (!nextAccessToken) {
                handleUnauthorized();
                return Promise.reject(error);
            }

            originalRequest._retry = true;
            originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${nextAccessToken}`,
            } as InternalAxiosRequestConfig['headers'];

            return client(originalRequest);
        },
    );
};

attachRequestInterceptor(api);
attachRequestInterceptor(axiosPrivate);
attachAuthExpiryInterceptor(api);
attachAuthExpiryInterceptor(axiosPrivate);

export default api;
